import React, { useState, useEffect } from 'react';
import { Button, Modal, message, Switch } from 'antd';
import styled from 'styled-components';
import InnerImageZoom from 'react-inner-image-zoom';

import withAuth from '~/libs/withAuth';
import ExpenseEditor from '~/components/ExpenseEditor';
import { updateExpense } from '~/libs/requests';
import { jsonFetcher, swrInvalidateMany } from '~/libs/helpers';
import { Accounts } from '~/libs/constants';

const StyledInnerImageZoom = styled(InnerImageZoom)`
	width: 100%;
	box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.35);
`;

const ModalContent = styled.div`
	display: flex;
`;

const ModalContentMedia = styled.div`
  max-width: 380px;
`;

const ModalContentForm = styled.div`
	flex: 2;
	margin-left: 20px;

	.ant-form-item-label {
    text-transform: uppercase;
    padding-bottom: 5px !important;
	}

	.ant-form-item-label label {
		color: gray;
		font-family: Lato;
		font-weight: 600;
	}
`;

const ModalTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding-right: 20px;
  padding-bottom: 20px;
  margin-bottom: 35px;
  border-bottom: 1px solid #e8e8e8;

  small {
    font-weight: normal;
  }
`;

const isPayment = (entry) => {
  
  // isWithheldTaxEntry
  if (entry.creditAccount === Accounts.LIABILITIES.DEPOSIT)
    return false;

  // Initial Payable Entry or Witheld Tax Entry or JCT Tax
  if (entry.creditAccount === Accounts.LIABILITIES.ACC_PAY)
    return false;

  return true;
}

const ExpenseModal = (props) => {
	const [ busy, setBusy ] = useState(false);
  const [ visible, setVisible ] = useState();
  const [ editExpense, setEditExpense ] = useState();

  const { expense, onClose } = props;

  useEffect(() => {
    if (expense) {
      setBusy(true);
      loadJournalEntries();
      setVisible(true);
    }
    else {
      setEditExpense();
      setVisible(false);
    }
  }, [ expense ]);

  const onIsExpertChange = (isExpert) => {
    setEditExpense({ ...expense, isExpert });
  }

  const loadJournalEntries = async () => {
    const journalEntries = await jsonFetcher(`/api/journal/${expense._id}`);
    let freshPayments = journalEntries.data.filter(x => isPayment(x, journalEntries));
    freshPayments = freshPayments.map(x => ({ 
      _id: x._id,
      date: x.date,
      year: x.year,
      month: x.month,
      day: x.day,
      creditSubAccountId: x.creditSubAccountId,
      amount: x.creditAmount,
      })).sort((a, b) => a.date - b.date);
    const freshExpense = { ...expense, payments: freshPayments };
    setEditExpense(freshExpense);
    setBusy(false);
  }

	const onExpenseUpdate = async (expense) => {
		setEditExpense(expense);
	}

	const onSaveClick = async () => {
		setBusy(true);
		const res = await updateExpense(editExpense);
		if (!res.success) {
			alert(res.message);
    }
		swrInvalidateMany('/api/expense');
    message.info("Updated an expense.");
    onClose();
		setBusy(false);
	}

	const onCancelClick = () => {
    onClose();
	}

	return (
    <Modal open={visible} width="90%" onOk={onSaveClick} onCancel={onCancelClick}
      title={<ModalTitleContainer>
        <div>
          <span>Edit Expense</span>
          <Switch style={{ marginLeft: 10 }} checkedChildren="Advanced" unCheckedChildren="Simple"
            checked={editExpense?.isExpert} onChange={() => { onIsExpertChange(!editExpense.isExpert) }} />
        </div>

        {editExpense?.isExpert && <div>
          <small style={{ marginLeft: 10 }}>JCT Tax (10%):</small>
          <Switch style={{ width: 60, marginLeft: 10 }} checkedChildren="Yes" unCheckedChildren="No"
            disabled={!editExpense || editExpense.isPaid}
            checked={editExpense?.jctTax > 0}
            onChange={() => { 
              onExpenseUpdate({...editExpense, jctTax: editExpense.jctTax ? 0 : 10 }); 
            }} />
          <small style={{ marginLeft: 10 }}>Paid:</small>
          <Switch style={{ marginLeft: 10, width: 60, marginRight: 10  }} disabled={!editExpense || !editExpense.amount}
            checkedChildren="Yes" unCheckedChildren="No" checked={editExpense?.isPaid} 
            onChange={(checked) => { onExpenseUpdate({...editExpense, isPaid: checked }); }} />
          {/* <Switch style={{ width: 120 }} checkedChildren="Splitted Payment" unCheckedChildren="One Payment" defaultChecked /> */}
        </div>}
      </ModalTitleContainer>}
      footer={[
        <Button key="back" onClick={onCancelClick}>Cancel</Button>,
        <Button key="submit" type="primary" loading={busy} onClick={onSaveClick}>Save Expense</Button>,
      ]}> 
      <ModalContent>

        <ModalContentMedia>
          {expense && expense.media && expense.media.thumbnails &&
            <StyledInnerImageZoom moveType='drag'
              src={expense.media.thumbnails.large} 
              zoomSrc={expense.media.thumbnails.large}
            />
          }
        </ModalContentMedia>
      
        <ModalContentForm>
          {editExpense && <ExpenseEditor expense={editExpense} onExpenseUpdate={onExpenseUpdate} />}
        </ModalContentForm>

      </ModalContent>
    </Modal>
	);

};

export default withAuth(ExpenseModal);